/**
 * AUTH58 Journey membership mutation helpers（leave／remove／transfer）。
 * 角色僅 OWNER｜COORDINATOR｜MEMBER；狀態僅 ACTIVE｜LEFT｜REMOVED。
 */

export const JOURNEY_MEMBER_ROLES = Object.freeze(['OWNER', 'COORDINATOR', 'MEMBER']);

export const JOURNEY_MEMBERSHIP_STATUSES = Object.freeze(['ACTIVE', 'LEFT', 'REMOVED']);

/** leave／remove 後端允許的旅程生命週期。 */
export const MEMBERSHIP_LEAVE_REMOVE_LIFECYCLES = Object.freeze([
  'DRAFT',
  'READY',
  'TRAVELING',
  'COMPLETED',
]);

const LEAVE_REMOVE_LIFECYCLE_SET = new Set(MEMBERSHIP_LEAVE_REMOVE_LIFECYCLES);

function sameMemberId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function isActive(status) {
  return status === 'ACTIVE';
}

function leaveRemoveLifecycleOk(lifecycleStatus) {
  return LEAVE_REMOVE_LIFECYCLE_SET.has(lifecycleStatus);
}

/**
 * ACTIVE COORDINATOR／MEMBER 可離開；OWNER 不可直接離開。
 * CANCELLED／ARCHIVED 不可離開。offline_cached 不可送出。
 */
export function canLeaveJourney({
  actorRole,
  actorStatus,
  lifecycleStatus,
  offlineCached = false,
} = {}) {
  if (offlineCached) return false;
  if (!isActive(actorStatus)) return false;
  if (actorRole === 'OWNER') return false;
  if (actorRole !== 'COORDINATOR' && actorRole !== 'MEMBER') return false;
  return leaveRemoveLifecycleOk(lifecycleStatus);
}

/** Owner 阻擋離開時的說明（繁中）。 */
export function ownerLeaveBlockedMessage({ actorRole, actorStatus } = {}) {
  if (isActive(actorStatus) && actorRole === 'OWNER') {
    return '你目前是旅程擁有者，需先轉移擁有權後才能離開旅程。';
  }
  return '';
}

export function leaveBlockedByLifecycleMessage(lifecycleStatus) {
  if (!leaveRemoveLifecycleOk(lifecycleStatus)) {
    return '目前旅程狀態無法離開或移除成員。';
  }
  return '';
}

/**
 * OWNER 可移除 ACTIVE COORDINATOR／MEMBER；
 * COORDINATOR 僅可移除 ACTIVE MEMBER。
 * 禁止 self／OWNER／LEFT／REMOVED／offline。
 */
export function canRemoveJourneyMember({
  actorRole,
  actorStatus,
  actorMemberId,
  targetRole,
  targetStatus,
  targetMemberId,
  lifecycleStatus,
  offlineCached = false,
} = {}) {
  if (offlineCached) return false;
  if (!isActive(actorStatus) || !isActive(targetStatus)) return false;
  if (!leaveRemoveLifecycleOk(lifecycleStatus)) return false;
  if (sameMemberId(actorMemberId, targetMemberId)) return false;
  if (targetRole === 'OWNER') return false;
  if (actorRole === 'OWNER') {
    return targetRole === 'COORDINATOR' || targetRole === 'MEMBER';
  }
  if (actorRole === 'COORDINATOR') {
    return targetRole === 'MEMBER';
  }
  return false;
}

/** Transfer：僅 ACTIVE OWNER；後端無 lifecycle gate，FE 不另造限制。 */
export function canTransferJourneyOwnership({
  actorRole,
  actorStatus,
  offlineCached = false,
} = {}) {
  if (offlineCached) return false;
  return isActive(actorStatus) && actorRole === 'OWNER';
}

export function canBeOwnershipTransferTarget({
  membershipStatus,
  role,
  memberId,
  ownerMemberId,
} = {}) {
  if (!isActive(membershipStatus)) return false;
  if (role !== 'COORDINATOR' && role !== 'MEMBER') return false;
  if (sameMemberId(memberId, ownerMemberId)) return false;
  return true;
}

export function eligibleOwnershipTransferTargets(members, ownerMemberId) {
  const list = Array.isArray(members) ? members : [];
  return list.filter((member) => canBeOwnershipTransferTarget({
    membershipStatus: member?.membershipStatus,
    role: member?.role,
    memberId: member?.memberId,
    ownerMemberId,
  }));
}

export function normalizeJourneyMembershipMutationResult(raw) {
  return {
    journeyId: raw?.journeyId == null ? null : String(raw.journeyId),
    memberId: raw?.memberId == null ? null : String(raw.memberId),
    role: raw?.role || '',
    membershipStatus: raw?.membershipStatus || '',
    journeyRowVersion: raw?.journeyRowVersion ?? null,
    noOp: Boolean(raw?.noOp),
    leftAt: raw?.leftAt ?? null,
    removedAt: raw?.removedAt ?? null,
    removalActorMemberId: raw?.removalActorMemberId == null
      ? null
      : String(raw.removalActorMemberId),
    removalReason: raw?.removalReason || null,
  };
}

export function normalizeOwnershipTransferResult(raw) {
  return {
    journeyId: raw?.journeyId == null ? null : String(raw.journeyId),
    previousOwnerMemberId: raw?.previousOwnerMemberId == null
      ? null
      : String(raw.previousOwnerMemberId),
    currentOwnerMemberId: raw?.currentOwnerMemberId == null
      ? null
      : String(raw.currentOwnerMemberId),
    rowVersion: raw?.rowVersion ?? null,
  };
}

/**
 * 供 Slice B People UX 對應錯誤家族；不發明後端不存在的 key。
 * @returns {'unauthorized'|'conflict'|'owner-protected'|'self-remove'|'invalid-lifecycle'|'forbidden'|'not-found'|'validation'|'network'|'error'}
 */
export function classifyMembershipMutationError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (key === 'travel.journey.membership.owner.protected'
      || key.includes('membership.owner.protected')) {
    return 'owner-protected';
  }
  if (key === 'travel.journey.membership.self.remove.forbidden'
      || key.includes('self.remove.forbidden')) {
    return 'self-remove';
  }
  if (key === 'travel.journey.concurrent.modification'
      || (code === 92003 && key.includes('concurrent'))) {
    return 'conflict';
  }
  if (key === 'travel.journey.invalid.lifecycle' || key.includes('invalid.lifecycle')) {
    return 'invalid-lifecycle';
  }
  if (code === 92002 || key === 'travel.journey.permission.denied' || key.includes('permission.denied')) {
    return 'forbidden';
  }
  if (code === 92001
      || key === 'travel.journey.not.found'
      || key === 'travel.journey.membership.not.found'
      || key.includes('not.found')) {
    return 'not-found';
  }
  if (code === 92000 || key.includes('validation')) {
    return 'validation';
  }
  if (!cause?.raw && /network|timeout|failed/i.test(String(cause?.message || ''))) {
    return 'network';
  }
  return 'error';
}

/** 繁體中文訊息；Slice B 可覆寫細節。 */
export function membershipMutationErrorMessage(kind, detail) {
  if (kind === 'conflict') {
    return '這趟旅程剛剛有新的變更，已重新載入最新狀態。';
  }
  if (kind === 'owner-protected') {
    return 'Owner 需先轉移所有權後才能離開；無法直接移除 Owner。';
  }
  if (kind === 'self-remove') {
    return '請使用「離開旅程」，無法透過移除操作移除自己。';
  }
  if (kind === 'invalid-lifecycle') {
    return detail || '目前旅程狀態無法變更成員。';
  }
  if (kind === 'forbidden') {
    return '你沒有權限執行此成員操作。';
  }
  if (kind === 'not-found') {
    return '找不到旅程或成員紀錄。';
  }
  if (kind === 'unauthorized') {
    return '登入狀態已失效，請重新登入。';
  }
  if (kind === 'validation') {
    return detail || '請確認後再試。';
  }
  if (kind === 'network') {
    return detail || '網路異常，請稍後再試。';
  }
  return detail || '無法更新旅程成員，請稍後再試。';
}
